import {
  Dropdown,
  DropdownItem,
  DropdownSeparator,
  DropdownToggle,
  Nav,
  NavGroup,
  NavItem,
  Page,
  PageHeader,
  PageHeaderTools,
  PageSidebar,
  Toolbar,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { NavLink } from 'react-router-dom'

import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import type { RootState } from '@app/state'

import type { ConnectedProps } from 'react-redux';

const mapState = (state: RootState) => ({
  account: state.user.account,
})

const connector = connect(mapState)
type PropsFromRedux = ConnectedProps<typeof connector>

interface IAppLayout {
  children?: React.ReactNode
}

type props = IAppLayout & PropsFromRedux

const PageLayout = function ({ children, account }: props) {
  const [isNavOpen, setIsNavOpen] = React.useState(true)
  const [isMobileView, setIsMobileView] = React.useState(true)
  const [isNavOpenMobile, setIsNavOpenMobile] = React.useState(false)
  const [isUserDropdownOpen, setIsUserDropdownOpen] = React.useState(false)

  const onNavToggleMobile = useCallback(
    () => setIsNavOpenMobile(!isNavOpenMobile),
    [isNavOpenMobile],
  )
  const closeNavMobileMenu = useCallback(() => {
    setIsNavOpenMobile(false)
  }, [])

  const onNavToggle = useCallback(() => setIsNavOpen(!isNavOpen), [isNavOpen])

  const onPageResize = useCallback(
    (props: { mobileView: boolean; windowSize: number }) => {
      setIsMobileView(props.mobileView)
    },
    [],
  )

  const onUserDropdownToggle = useCallback(
    () => setIsUserDropdownOpen(!isUserDropdownOpen),
    [isUserDropdownOpen],
  )

  const userDropdownItems = useMemo(
    () => [
      <DropdownItem key="account">
        <NavLink exact to={'/account/settings'}>
          My Account
        </NavLink>
      </DropdownItem>,
      <DropdownSeparator key="separator" />,
      <DropdownItem key="logout">
        <NavLink exact to={'/logout'}>
          Sign Out
        </NavLink>
      </DropdownItem>,
      <RequireFeatureFlag
        key="testfeature_flag_enabled"
        feature={FeatureFlagId.FeatureFlagTest}
      >
        <DropdownItem>
          <span>Test feature flag enabled</span>
        </DropdownItem>
      </RequireFeatureFlag>,
    ],
    [],
  )

  const PageToolbar = (
    <PageHeaderTools>
      <Toolbar>
        <ToolbarGroup>
          <ToolbarItem>
            <Dropdown
              isPlain
              position="right"
              toggle={
                <DropdownToggle onToggle={onUserDropdownToggle}>
                  {account
                    ? `${account.givenName} ${account.familyName}`
                    : `Loading...`}
                </DropdownToggle>
              }
              isOpen={isUserDropdownOpen}
              dropdownItems={userDropdownItems}
            />
          </ToolbarItem>
        </ToolbarGroup>
      </Toolbar>
    </PageHeaderTools>
  )

  const Header = (
    <PageHeader
      logo="Cohesic Intake"
      headerTools={PageToolbar}
      showNavToggle
      isNavOpen={isNavOpen}
      onNavToggle={isMobileView ? onNavToggleMobile : onNavToggle}
    />
  )

  const Navigation = (
    <Nav id="nav-primary-simple" theme="dark">
      <NavGroup title="Clinic">
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/clinic/today'} activeClassName="pf-m-current">
            Today
          </NavLink>
        </NavItem>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/clinic/visits'} activeClassName="pf-m-current">
            Visits
          </NavLink>
        </NavItem>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/clinic/patients'} activeClassName="pf-m-current">
            Patients
          </NavLink>
        </NavItem>
        <RequireFeatureFlag
          key="program_configuration_enabled"
          feature={FeatureFlagId.ProgramConfiguration}
        >
          <NavItem onClick={closeNavMobileMenu}>
            <NavLink
              exact
              to={'/clinic/programs'}
              activeClassName="pf-m-current"
            >
              Programs
            </NavLink>
          </NavItem>
        </RequireFeatureFlag>
      </NavGroup>
      <NavGroup title="Administration">
        <RequireFeatureFlag feature={FeatureFlagId.DataIntegrations}>
          <NavItem onClick={closeNavMobileMenu}>
            <NavLink
              exact
              to={'/clinic/integrations'}
              activeClassName="pf-m-current"
            >
              Integrations
            </NavLink>
          </NavItem>
        </RequireFeatureFlag>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/admin/patients'} activeClassName="pf-m-current">
            Patients
          </NavLink>
        </NavItem>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/admin'} activeClassName="pf-m-current">
            Settings
          </NavLink>
        </NavItem>
      </NavGroup>
    </Nav>
  )
  const Sidebar = (
    <PageSidebar
      theme="dark"
      nav={Navigation}
      isNavOpen={isMobileView ? isNavOpenMobile : isNavOpen}
    />
  )
  return (
    <Page
      mainContainerId="primary-app-container"
      header={Header}
      sidebar={Sidebar}
      onPageResize={onPageResize}
    >
      {children}
    </Page>
  )
}

export default connector(PageLayout)
